import { useEffect, useLayoutEffect, useSyncExternalStore } from 'react';
import { useLocalPrefs, useMe } from '@/model';
import { i18next, setLanguage } from '@/i18n';
import { applyTheme } from '@/ui';
import type { Navigation } from './contract';
import { useLoginSession } from './login-session';

const subscribeWidth = (listener: () => void) => {
  window.addEventListener('resize', listener);
  return () => window.removeEventListener('resize', listener);
};
const getWidth = () => window.innerWidth;

export function useLayout(): Navigation['layout'] {
  const width = useWindowWidth();
  return width < 768 ? 'phone' : width < 1024 ? 'split' : 'mac';
}

export function useWindowWidth(): number {
  return useSyncExternalStore(subscribeWidth, getWidth, getWidth);
}

export function Environment() {
  const prefs = useLocalPrefs();
  const me = useMe();
  const { session } = useLoginSession();
  const layout = useLayout();
  const messageWidth = 'messageWidth' in prefs && typeof prefs.messageWidth === 'string' ? prefs.messageWidth : 'standard';
  const wallpaper = 'wallpaper' in prefs && typeof prefs.wallpaper === 'string' ? prefs.wallpaper : 'standard';
  // Only the current account's profile may override its locally published pack.
  const accent = session.phase === 'signedIn' && me?.account.id === session.accountId ? me.settings.accent : null;
  const appearance = (value: unknown, fallback: string) => session.phase === 'signedIn' && typeof value === 'string' ? value : fallback;
  const themePack = appearance(prefs.themePack, 'calm'), density = appearance(prefs.density, 'default'), messageLayout = appearance(prefs.messageLayout, 'default');
  useLayoutEffect(() => {
    const root = document.documentElement;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      const theme = prefs.theme === 'system' ? query.matches ? 'dark' : 'light' : prefs.theme;
      const options = { theme, accent, textSize: prefs.textSize, messageWidth, wallpaper, layout, themePack, density, messageLayout };
      applyTheme(options, root);
      root.dataset.hyloTheme = theme;
      root.dataset.hyloLayout = layout;
      root.dataset.hyloTextSize = root.dataset.textSize;
      root.dataset.hyloMessageWidth = root.dataset.messageWidth;
      root.dataset.hyloWallpaper = root.dataset.wallpaper;
      root.style.colorScheme = theme;
      const background = getComputedStyle(root).backgroundColor;
      const themeColor = background && !['transparent', 'rgba(0, 0, 0, 0)'].includes(background) ? background : theme === 'dark' ? '#0B0B12' : '#F8F8FB';
      document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach(meta => { meta.content = themeColor; });
    };
    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, [accent, density, layout, messageLayout, messageWidth, prefs.textSize, prefs.theme, themePack, wallpaper]);
  useEffect(() => {
    setLanguage(prefs.language);
    const apply = () => { document.documentElement.lang = i18next.resolvedLanguage ?? 'en'; };
    apply();
    i18next.on('languageChanged', apply);
    return () => { i18next.off('languageChanged', apply); };
  }, [prefs.language]);
  useEffect(() => {
    const viewport = window.visualViewport;
    const apply = () => {
      const root = document.documentElement;
      root.style.setProperty('--hylo-viewport-height', `${viewport?.height ?? window.innerHeight}px`);
      root.style.setProperty('--hylo-viewport-top', `${viewport?.offsetTop ?? 0}px`);
      root.style.setProperty('--hylo-keyboard-inset', `${Math.max(0, window.innerHeight - (viewport?.height ?? window.innerHeight) - (viewport?.offsetTop ?? 0))}px`);
    };
    apply();
    viewport?.addEventListener('resize', apply);
    viewport?.addEventListener('scroll', apply);
    window.addEventListener('resize', apply);
    return () => {
      viewport?.removeEventListener('resize', apply);
      viewport?.removeEventListener('scroll', apply);
      window.removeEventListener('resize', apply);
    };
  }, []);
  return null;
}
